import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { ScrollTrigger } from "gsap/ScrollTrigger";
import '../health.scss';
import healthMetricsChart from './../../assets/images/health-future-pie-chart.png';
import healthSquare from './../../assets/images/health-shapes-square.svg';
import healthTriangle from './../../assets/images/health-shapes-triangle.svg';
import { gsap } from 'gsap';
import AOS from "aos";
import "aos/dist/aos.css";

export default function HealthUsage() {

  const square1Ref = useRef();
  const square2Ref = useRef();
  const triangle1Ref = useRef();
  const triangle2Ref = useRef();

  gsap.registerPlugin(ScrollTrigger);

  useEffect(() => {

    document.addEventListener('aos:in:bar-anim', function(ev) {
      //console.log('animated in', ev);

      if (ev.detail.querySelectorAll('.progress').length > 1) {
        console.log("more than 1 progress");
        var barArray = [...ev.detail.querySelectorAll('.progress')];
      } else {
        console.log("less than 1 progress");
        var barArray = [];
        var bar = ev.detail.querySelector('.progress');
        console.log(bar);
        barArray.push(bar);
      }

      console.log(barArray);

      if (barArray && barArray.length) {
        barArray.forEach(function(barItem) {
          var fill = barItem.getAttribute('data-fill');

          if (!ev.detail.classList.contains('vertical-bar-chart')) {
            barItem.style.width = fill + '%';
          }
        });
      }

      var Cont={val:0};

      if (ev.detail.querySelectorAll('.percentage').length > 1) {
        var percentageArray = [...ev.detail.querySelectorAll('.percentage')];
        console.log("more than 1 percentage");
      } else {
        var percentageArray = [];
        var percentage = ev.detail.children[0].querySelector('.percentage');
        percentageArray.push(percentage);
        console.log("less than percentage");

      }

      //console.log(percentage);

      if (percentageArray) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }

      console.log(percentageArray);

    });

    document.addEventListener('aos:in:percentage', function(ev) {
      var Cont={val:0};
      var percentage = ev.detail.children[0].querySelector('.percentage');
      var percentageArray = [];
      percentageArray.push(percentage);
      //console.log(percentage);

      if (percentage) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }
    });

    // Had to make some adjustments since it seemed like the animation wasn't playing nice with React. The shapes weren't incrementing exactly 90 degrees (it was adding a few degrees with decimal places) so they always rotated crooked. Possibly React strict mode causing issues. Added gsap.context, refs, and using GSAP's ScrollTrigger instead of AOS. 
    let ctx = gsap.context(() => {

      var tl = gsap.timeline({ 
        onComplete:rotateAgain,
        scrollTrigger: {
          trigger: ".shape-images"
        }
      });

      function animate(){
        tl
        .to(triangle1Ref.current, 0.5, {rotation:"+=90"})
        .to(square1Ref.current, 0.5, {rotation:"+=90"}, "+=2")
        .to(triangle2Ref.current, 0.5, {rotation:"+=90"}, "+=2")
        .to(square2Ref.current, 0.5, {rotation:"+=90"}, "+=2")
      }

      function rotateAgain(){
        gsap.delayedCall(5, animate)
      }

      animate();

    });

    return () => { ctx.revert(); /* <- cleanup! */ };

    AOS.init({
      once: true
    });
    AOS.refresh();
  }, []);

  return (
    <>
      <div className="health-future">

        <p id="s1" className="intro" data-aos="fade-up">Explore the data to discover how businesses in the health industry plan to use direct mail going forward.&#8288;<a href="#reference-1" class="cm-article-content__reference" aria-label="Footnote 1" title="Footnote 1"><sup aria-hidden="true">[1]</sup></a></p>
        <div className='two-cols'>
          <div className='col-left'>
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Motivators for More Mail Use*</h3>

                <p className="pull-quote no-margin-bottom"><span data-aos className='highlighter'>Easier audience targeting</span></p>
                <p>Health</p>

                <ul className='no-icons'>
                  <li>Financial — Ability to track campaigns from print to delivery</li>
                  <li>Insurance — Easier personalization</li>
                  <li>Retail — Higher return on ad spend (ROAS)</li>
                </ul>

                <p className="disclaimer">*Consumer packaged goods companies had too low a sample size to be included in this part of the survey.</p>
                <p>More than companies in any other industry, health businesses say that easier targeting would be the main motivator for increasing mail use going forward.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>The Every Door Direct Mail<sup>&reg;</sup> service can make it easy to target local patients or customers—as well as prospects—and form meaningful connections with people in your community. <strong><a href="/../every-door-direct-mail-101-an-introduction-for-businesses/">Explore this quick four-step tutorial</a></strong> to see how it works and how to get started.</p>
              </div>
            </div>

            <div className='inner-cols'>
              <div className='inner-col-item'>
                <div className='rounded-box-module-wrap'>
                  <div className="rounded-box-module" data-aos="fade-up">
                    <h3>Impediments to Mail Use</h3>  
                    <div className='progress-bar-area'>
                      <div className='progressbar-item'>
                        <span className='label'>Cost per mailing/too expensive</span>
                        <div className='progress-bar size-small' data-aos data-aos-id="bar-anim">
                          <div className='progress' data-fill="35"><span class="percentage" data-to="35" aria-hidden="true">0</span><span class="screen-reader-text">35</span>%</div>
                        </div>
                      </div>
                    </div>
                    <p>Overall, 35% of companies across all industries report that cost per mailing, and direct mail being too expensive generally, are the main impediments to using mail. Health businesses also report this as the main reason for not using mail more often.</p>
                  </div>
                  <div className="rounded-box-module">
                    <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                    <p id="s3">Direct mail can be an extremely effective marketing channel: Nearly seven out of 10 marketers feel direct mail is the highest ROI marketing channel at their disposal.&#8288;<a href="#reference-3" class="cm-article-content__reference" aria-label="Footnote 3" title="Footnote 3"><sup aria-hidden="true">[3]</sup></a> To keep down mailing costs, make sure there are no repeats on your mailing lists, so you don’t waste money sending out multiple mailpieces to the same address (and don’t overwhelm your audience).</p>
                  </div>
                </div>
              </div>
              <div className='inner-col-item'>
                <div className='personalized-box secondary align-to-text'>
                  <h3>Removing duplicates from mailing lists can help keep costs down.</h3>
                  <div className='shape-images'>
                    <div className='triangle triangle-1'>
                      <img ref={triangle1Ref} src={healthTriangle} alt="" />
                    </div>
                    <div className='square square-1'>
                      <img ref={square1Ref} src={healthSquare} alt="" />
                    </div>
                    <div className='triangle triangle-2'>
                      <img ref={triangle2Ref} src={healthTriangle} alt="" />
                    </div>
                    <div className='square square-2'>
                      <img ref={square2Ref} src={healthSquare} alt="" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='col-right'>
            
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Using Direct Mail in the Future</h3>
                <div className='pie-chart' data-aos>
                  <svg viewBox="0 0 32 32" role="img" alt="" title="">
                    <circle r="16" cx="16" cy="16" strokeDasharray="0 100"></circle>
                  </svg>
                </div>

                <span data-aos data-aos-id="percentage" data-aos-anchor=".pie-chart">
                  <span data-aos className='percentage-value'><span class="percentage" data-to="92" aria-hidden="true">0</span><span class="screen-reader-text">92</span>%</span>
                </span>

                <p>More or the same</p>
                <p>The vast majority of health companies say they are likely to use direct mail in the future, with 92% saying they plan to use it the same amount or more in future marketing campaigns.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p id="s2">In an uncertain economy and ever-evolving market, mail can help maintain stability—and increase ROI. In fact, mail campaigns using standard letter-sized mail had a 112% average ROI when sent out to prospects—higher than SMS texts (at 102%), email (at 93%) and paid search (at 88%).&#8288;<a href="#reference-2" class="cm-article-content__reference" aria-label="Footnote 2" title="Footnote 2"><sup aria-hidden="true">[2]</sup></a> Automating campaigns, relying on data and reducing waste can all help ensure long-term direct mail success.</p>
              </div>
            </div> 
          </div>
        </div>
      </div>
    </>
  );
}
